import type { BillingPeriod, TmsPaidTier, TmsTier } from '@numeo/types-core'
import { isAxiosError } from 'axios'
import { isRecord } from 'utils/isRecord'
import { getTmsPrices } from './tmsBilling.api'

/**
 * Thrown by a create flow when a per-truck seat limit was hit and already surfaced to the user
 * (an actionable snackbar). Callers should swallow it — no generic error toast, no success toast.
 */
export class SeatOverageHandledError extends Error {
    constructor() {
        super('TMS seat overage handled')
        this.name = 'SeatOverageHandledError'
    }
}

const perTruckPhrase = (cents: number, billingInterval?: BillingPeriod): string =>
    `$${(cents / 100).toFixed(2)}/${billingInterval === 'annual' ? 'yr' : 'mo'} per extra truck`

/**
 * Resolve the exact per-truck overage price for the seat-limit prompts (ADR-020).
 *
 * `subscriptionCents` is this subscription's OWN rate (TmsSubscription.extraTruckCents) and always
 * wins: the /tms/prices catalog is tier-global and deliberately skips gluon-minted per-customer
 * prices, so quoting from it shows a customer on a negotiated rate the list price — e.g. $14.99
 * against a real $5.00 — while Stripe correctly bills the negotiated one. The catalog is the
 * fallback for everyone else, and a generic phrase if pricing can't load, so a pricing hiccup
 * never blocks the prompt.
 */
export const formatExtraTruckPrice = async (tier?: TmsTier, billingInterval?: BillingPeriod, subscriptionCents?: number | null): Promise<string> => {
    if (subscriptionCents != null) return perTruckPhrase(subscriptionCents, billingInterval)
    try {
        if (!tier) return 'the per-truck rate'
        const prices = await getTmsPrices()
        const plan = prices.data?.[tier]
        const cents = (billingInterval === 'annual' ? plan?.extraTruckAnnualCents : plan?.extraTruckMonthlyCents) ?? null
        if (cents == null) return 'the per-truck rate'
        return perTruckPhrase(cents, billingInterval)
    } catch {
        return 'the per-truck rate'
    }
}

type TmsTruckSeatLimitCode = 'TMS_SEAT_OVERAGE_CONFIRM' | 'TMS_SEAT_LIMIT_TRIAL'

interface TmsTruckSeatLimitData {
    currentTrucks?: number
    purchasedTrucks?: number
    requestedCount?: number
    newTruckTotal?: number
    tier?: TmsPaidTier
    billingInterval?: BillingPeriod
    /** The subscription's own per-truck rate, when the server knows it — preferred over the catalog. */
    extraTruckCents?: number
}

interface TmsTruckSeatLimitError {
    code: TmsTruckSeatLimitCode
    message: string
    data: TmsTruckSeatLimitData
}

export interface TmsTruckSeatLimitPrompt {
    kind: 'active' | 'trial'
    title: string
    message: string
    actionLabel: string
    secondaryLabel?: string
}

interface PromptCopy {
    activeTitle: string
    activeActionLabel: string
    trialTitle?: string
    trialActionLabel?: string
    trialSecondaryLabel?: string
}

const isTmsTruckSeatLimitCode = (value: unknown): value is TmsTruckSeatLimitCode => value === 'TMS_SEAT_OVERAGE_CONFIRM' || value === 'TMS_SEAT_LIMIT_TRIAL'

const numberValue = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined)

const tmsTierValue = (value: unknown): TmsPaidTier | undefined => (value === 'basic' || value === 'pro' ? value : undefined)

const billingPeriodValue = (value: unknown): BillingPeriod | undefined => (value === 'monthly' || value === 'annual' ? value : undefined)

export const getTmsTruckSeatLimitError = (error: unknown): TmsTruckSeatLimitError | null => {
    if (!isAxiosError(error)) return null

    const body = error.response?.data
    if (!isRecord(body) || !isTmsTruckSeatLimitCode(body.error)) return null
    const data = isRecord(body.data) ? body.data : {}
    const message = typeof body.message === 'string' && body.message.trim() ? body.message : 'Your TMS truck limit was reached.'

    return {
        code: body.error,
        message,
        data: {
            currentTrucks: numberValue(data.currentTrucks),
            purchasedTrucks: numberValue(data.purchasedTrucks),
            requestedCount: numberValue(data.requestedCount),
            newTruckTotal: numberValue(data.newTruckTotal),
            tier: tmsTierValue(data.tier),
            billingInterval: billingPeriodValue(data.billingInterval),
            extraTruckCents: numberValue(data.extraTruckCents),
        },
    }
}

export const buildTmsTruckSeatLimitPrompt = async (error: unknown, copy: PromptCopy): Promise<TmsTruckSeatLimitPrompt | null> => {
    const limit = getTmsTruckSeatLimitError(error)
    if (!limit) return null

    if (limit.code === 'TMS_SEAT_LIMIT_TRIAL') {
        return {
            kind: 'trial',
            title: copy.trialTitle ?? 'Truck limit reached',
            message: limit.message,
            actionLabel: copy.trialActionLabel ?? 'Upgrade plan',
            secondaryLabel: copy.trialSecondaryLabel,
        }
    }

    const price = await formatExtraTruckPrice(limit.data.tier, limit.data.billingInterval, limit.data.extraTruckCents)
    return {
        kind: 'active',
        title: copy.activeTitle,
        message: `${limit.message} Rate: ${price}.`,
        actionLabel: copy.activeActionLabel,
    }
}
